import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  Download, Upload, RefreshCw, HardDrive, Package, Archive,
  CheckCircle2, AlertTriangle, Shield, Printer, ExternalLink,
} from 'lucide-react';

const ARTIFACTS = [
  {
    id: 'desktop_zip',
    labelKey: 'adminDownloads.artifactDesktopLabel',
    descKey: 'adminDownloads.artifactDesktopDesc',
    suffix: '.zip',
    download_link_endpoint: '/desktop/app/download-link',
    info_endpoint: '/desktop/app/info',
    icon: Package,
  },
  {
    id: 'agent_pack',
    labelKey: 'adminDownloads.artifactPackLabel',
    descKey: 'adminDownloads.artifactPackDesc',
    suffix: '.zip',
    download_link_endpoint: '/printer/agent/pack-link',
    info_endpoint: '/printer/agent/pack-info',
    icon: Archive,
  },
  {
    id: 'agent_exe',
    labelKey: 'adminDownloads.artifactExeLabel',
    descKey: 'adminDownloads.artifactExeDesc',
    suffix: '.exe',
    download_link_endpoint: '/printer/agent/exe-link',
    info_endpoint: '/printer/agent/exe-info',
    icon: HardDrive,
  },
];

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const ArtifactCard = ({
  artifact,
  file,
  onFileChange,
  onUpload,
  onDownload,
  uploading,
  canUpload,
}) => {
  const { t, formatDate } = useLanguage();
  const inputId = `artifact-upload-${artifact.id}`;
  const Icon = artifact.icon;

  return (
    <div className="rounded-xl border border-white/5 bg-[#111827] p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#009b3a]/15 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[#00d946]" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">{t(artifact.labelKey)}</h3>
            <p className="text-xs text-gray-500 mt-1">{t(artifact.descKey)}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${
                  artifact.available
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                    : 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                }`}
              >
                {artifact.available ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {artifact.available ? t('adminDownloads.available') : t('adminDownloads.missing')}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-white/10 bg-white/5 text-gray-300">
                <Archive className="w-3 h-3" />
                {artifact.suffix}
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onDownload(artifact)}
          disabled={!artifact.available}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-xs font-medium text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download className="w-3.5 h-3.5" />
          {t('adminDownloads.download')}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
          <p className="text-gray-500 mb-1">{t('adminDownloads.activeFile')}</p>
          <p className="text-white break-all">{artifact.filename || '—'}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
          <p className="text-gray-500 mb-1">{t('adminDownloads.size')}</p>
          <p className="text-white">{formatBytes(artifact.size_bytes)}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
          <p className="text-gray-500 mb-1">{t('adminDownloads.lastUpdate')}</p>
          <p className="text-white">
            {(artifact.updated_at || artifact.filesystem_updated_at)
              ? formatDate(artifact.updated_at || artifact.filesystem_updated_at)
              : '—'}
          </p>
        </div>
        <div className="rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
          <p className="text-gray-500 mb-1">{t('adminDownloads.uploadedBy')}</p>
          <p className="text-white">{artifact.updated_by || '—'}</p>
        </div>
      </div>

      {canUpload && (
        <div className="mt-4 rounded-lg border border-white/5 bg-[#0a0f1c] p-3">
          <label htmlFor={inputId} className="block text-xs text-gray-400 mb-2">
            {t('adminDownloads.replaceLiveFile')}
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              key={`${artifact.id}-${artifact.updated_at || artifact.filesystem_updated_at || 'new'}`}
              id={inputId}
              type="file"
              accept={artifact.suffix}
              onChange={(e) => onFileChange(artifact.id, e.target.files?.[0] || null)}
              className="block w-full text-xs text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-[#009b3a] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-[#00b344]"
            />
            <button
              type="button"
              onClick={() => onUpload(artifact)}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#009b3a] text-white text-sm font-medium hover:bg-[#00b344] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              {uploading ? t('adminDownloads.uploading') : t('adminDownloads.replace')}
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            {t('adminDownloads.replaceHint')}
          </p>
          {file && (
            <p className="text-[11px] text-cyan-300 mt-2">
              {t('adminDownloads.selectedFile', { name: file.name })}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 text-[11px] text-gray-500 break-all">
        SHA256: {artifact.sha256 || '—'}
      </div>
    </div>
  );
};

const AdminDownloadsPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const canUpload = user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [files, setFiles] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const results = await Promise.all(ARTIFACTS.map(async (artifact) => {
        const [infoRes] = await Promise.allSettled([api.get(artifact.info_endpoint)]);
        const info = infoRes.status === 'fulfilled' ? (infoRes.value?.data || {}) : {};
        return {
          ...artifact,
          ...info,
        };
      }));
      setArtifacts(results);
    } catch (err) {
      setError(err.response?.data?.detail || t('adminDownloads.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const available = artifacts.filter((a) => a.available).length;
    const size = artifacts.reduce((sum, a) => sum + (a.size_bytes || 0), 0);
    return { available, size };
  }, [artifacts]);

  const handleDownload = async (artifact) => {
    setMessage('');
    setError('');
    try {
      const res = await api.get(artifact.download_link_endpoint);
      const url = res?.data?.url;
      if (!url) throw new Error(t('adminDownloads.downloadLinkUnavailable'));
      window.location.assign(url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('adminDownloads.downloadFailed'));
    }
  };

  const handleFileChange = (artifactId, file) => {
    setFiles((prev) => ({ ...prev, [artifactId]: file || null }));
  };

  const handleUpload = async (artifact) => {
    const file = files[artifact.id];
    if (!file) {
      setError(t('adminDownloads.selectFilePrompt', { label: t(artifact.labelKey) }));
      return;
    }
    if (!window.confirm(t('adminDownloads.confirmReplace', { label: t(artifact.labelKey), name: file.name }))) return;

    setUploadingId(artifact.id);
    setMessage('');
    setError('');
    try {
      const form = new FormData();
      form.append('artifact_id', artifact.id);
      form.append('file', file);
      await api.post('/super-admin/packages/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage(t('adminDownloads.replaceSuccess', { label: t(artifact.labelKey) }));
      setFiles((prev) => ({ ...prev, [artifact.id]: null }));
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('adminDownloads.uploadError'));
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in p-2 sm:p-4 space-y-6" data-testid="admin-downloads-page">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#009b3a]/15 flex items-center justify-center">
                <Download className="w-5 h-5 text-[#00d946]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{t('adminDownloads.title')}</h1>
                <p className="text-sm text-gray-400">{t('adminDownloads.subtitle')}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to="/admin/settings/printer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-sm text-white hover:bg-white/10 transition-colors"
              >
                <Printer className="w-4 h-4" />
                {t('adminDownloads.printerConfig')}
              </Link>
              <button
                type="button"
                onClick={async () => {
                  setRefreshing(true);
                  await load();
                  setRefreshing(false);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-sm text-white hover:bg-white/10 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t('adminDownloads.refresh')}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/5 bg-[#111827] p-4">
            <p className="text-xs text-gray-500">{t('adminDownloads.availableArtifacts')}</p>
            <p className="text-2xl font-bold text-white mt-1">{totals.available}/{artifacts.length || 3}</p>
          </div>
          <div className="rounded-xl border border-white/5 bg-[#111827] p-4">
            <p className="text-xs text-gray-500">{t('adminDownloads.totalSize')}</p>
            <p className="text-2xl font-bold text-white mt-1">{formatBytes(totals.size)}</p>
          </div>
          <div className="rounded-xl border border-white/5 bg-[#111827] p-4">
            <p className="text-xs text-gray-500">{t('adminDownloads.alert')}</p>
            <p className="text-sm text-gray-300 mt-1">
              {t('adminDownloads.alertText')}
            </p>
          </div>
        </div>

        {message && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            {message}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64 rounded-xl border border-white/5 bg-[#111827]">
            <div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {artifacts.map((artifact) => (
              <ArtifactCard
                key={artifact.id}
                artifact={artifact}
                file={files[artifact.id]}
                onFileChange={handleFileChange}
                onUpload={handleUpload}
                onDownload={handleDownload}
                uploading={uploadingId === artifact.id}
                canUpload={canUpload}
              />
            ))}
          </div>
        )}

        <div className="rounded-xl border border-white/5 bg-[#111827] p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-[#00d946] mt-0.5" />
          <div className="text-sm text-gray-300">
            <p className="font-medium text-white">{t('adminDownloads.accessTitle')}</p>
            <p className="text-gray-500 mt-1">
              {t('adminDownloads.accessText')}
            </p>
            <p className="text-gray-500 mt-1 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              {t('adminDownloads.signedLinksNote')}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDownloadsPage;
